// Custom contact groups. A group is a user-created label (Famille, Amis
// proches, …); each contact can belong to zero, one, or several. Groups let the
// Cercle screen organise and filter contacts along an axis the user controls,
// independent of connection status (mutual/pending) or manual verification.
//
// The canonical group list lives locally (in the identity store under one key)
// so empty groups persist and their order is stable; membership is stored on
// each Friend as an array of group IDs (see core/friends). IDs — not names — are
// the reference, so renaming a group never has to rewrite every contact.
//
// Like contacts, groups carry a change timestamp and are removed as tombstones
// so the user's devices can reconcile (ARCHITECTURE.md §8.1).

import { idbGet, idbPut, STORE_IDENTITY } from './db';
import { listFriends, setFriendGroups } from './friends';

export interface Group {
  id: string;
  name: string;
  createdAt: number;
  /** Epoch ms of the last local change — the clock reconciliation compares. */
  updatedAt?: number;
  /** Epoch ms of removal. Set = tombstone: hidden locally, still carried. */
  deletedAt?: number;
}

export class GroupError extends Error {}

const GROUPS_KEY = 'groups';

function genId(): string {
  return 'g_' + Array.from(crypto.getRandomValues(new Uint32Array(2)), (n) => n.toString(36)).join('');
}

/** When this group last changed, for reconciliation. See friendStamp. */
export function groupStamp(g: Group): number {
  return Math.max(g.deletedAt ?? 0, g.updatedAt ?? 0, g.createdAt ?? 0);
}

export function isDeleted(g: Group): boolean {
  return g.deletedAt !== undefined;
}

/** Every entry including tombstones — for the backup and for reconciliation. */
async function allGroupEntries(): Promise<Group[]> {
  return (await idbGet<Group[]>(STORE_IDENTITY, GROUPS_KEY)) ?? [];
}

export function listGroupEntries(): Promise<Group[]> {
  return allGroupEntries();
}

/** The visible groups, oldest first (stable creation order). */
export async function listGroups(): Promise<Group[]> {
  const stored = await allGroupEntries();
  return stored
    .filter((g) => !isDeleted(g))
    .slice()
    .sort((a, b) => a.createdAt - b.createdAt);
}

async function saveGroups(groups: Group[]): Promise<void> {
  await idbPut(STORE_IDENTITY, GROUPS_KEY, groups);
}

export async function createGroup(name: string): Promise<Group> {
  const trimmed = name.trim();
  if (!trimmed) throw new GroupError('Donnez un nom au groupe');
  const entries = await allGroupEntries();
  if (entries.some((g) => !isDeleted(g) && g.name.toLowerCase() === trimmed.toLowerCase())) {
    throw new GroupError('Un groupe porte déjà ce nom');
  }
  const now = Date.now();
  const group: Group = { id: genId(), name: trimmed, createdAt: now, updatedAt: now };
  await saveGroups([...entries, group]);
  return group;
}

export async function renameGroup(id: string, name: string): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed) throw new GroupError('Donnez un nom au groupe');
  const entries = await allGroupEntries();
  if (entries.some((g) => g.id !== id && !isDeleted(g) && g.name.toLowerCase() === trimmed.toLowerCase())) {
    throw new GroupError('Un groupe porte déjà ce nom');
  }
  await saveGroups(entries.map((g) => (g.id === id ? { ...g, name: trimmed, updatedAt: Date.now() } : g)));
}

/**
 * Delete a group and drop it from every contact that referenced it. Kept as a
 * tombstone so the deletion reaches the user's other devices.
 */
export async function deleteGroup(id: string): Promise<void> {
  const entries = await allGroupEntries();
  const now = Date.now();
  await saveGroups(entries.map((g) => (g.id === id ? { ...g, deletedAt: now, updatedAt: now } : g)));
  const friends = await listFriends();
  await Promise.all(
    friends
      .filter((f) => f.groups?.includes(id))
      .map((f) => setFriendGroups(f.circleId, (f.groups ?? []).filter((x) => x !== id))),
  );
}

/** Drop tombstones for good, once they are too old to still be news anywhere. */
export async function purgeGroups(olderThan: number): Promise<void> {
  const entries = await allGroupEntries();
  const kept = entries.filter((g) => !isDeleted(g) || (g.deletedAt ?? 0) > olderThan);
  if (kept.length !== entries.length) await saveGroups(kept);
}

/**
 * Deterministic tie-break for two entries changed at the exact same instant.
 * See friends.ts's `wins` — same rule, kept local to each module since
 * neither imports UI-independent shared utilities for a two-line helper.
 */
function wins<T>(candidate: T, other: T): boolean {
  return JSON.stringify(candidate) > JSON.stringify(other);
}

/**
 * Reconcile groups from a self-backup with the local ones, entry by entry: the
 * more recently changed side wins, tombstones included. See reconcileFriends.
 * Returns how many entries changed locally.
 */
export async function reconcileGroups(incoming: Group[]): Promise<number> {
  const entries = await allGroupEntries();
  const byId = new Map(entries.map((g) => [g.id, g]));
  let changed = 0;
  for (const remote of incoming) {
    const mine = byId.get(remote.id);
    if (mine) {
      const cmp = groupStamp(remote) - groupStamp(mine);
      if (cmp < 0 || (cmp === 0 && !wins(remote, mine))) continue;
    }
    byId.set(remote.id, remote);
    changed++;
  }
  if (changed > 0) await saveGroups([...byId.values()]);
  return changed;
}

/** How many contacts belong to each group, keyed by group ID. */
export async function groupCounts(): Promise<Map<string, number>> {
  const friends = await listFriends();
  const counts = new Map<string, number>();
  for (const f of friends) for (const gid of f.groups ?? []) counts.set(gid, (counts.get(gid) ?? 0) + 1);
  return counts;
}
